<template>
	<div class="mg-b-20 mg-lg-b-25 mg-xl-b-30">
		<div v-if="loading">
			<spinner-component :loading="loading"/>
		</div>

		<div v-if="error" class="tx-center tx-danger">
			{{ error }}
		</div>

		<div v-else class="row justify-content-center">
            <div v-if="user" class="col-lg-8 pd-lg-x-0 mb-5">
                <alert-success :form="form" :class="'shadow-sm'" message="Your changes have been saved!"></alert-success>

                <h3 class="tx-color-01 mg-b-10">Personal Details</h3>
                <p class="tx-color-03 mg-b-10">Please provide us with your personal details.</p>

                <form class="row" @submit.prevent="save">
                    <div class="form-group col-md-6">
						<label>First Name</label>
						<input v-model="form.first_name" type="text" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('first_name') }" placeholder="Enter your first name" required>
						<has-error :form="form" field="first_name"></has-error>
					</div>
                    <div class="form-group col-md-6">
						<label>Surname</label>
						<input v-model="form.last_name" type="text" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('last_name') }" placeholder="Enter your last name" required>
						<has-error :form="form" field="last_name"></has-error>
					</div>
                    <div class="form-group col-md-6">
						<label>Other Names</label>
						<input v-model="form.additional_names" type="text" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('additional_names') }" placeholder="Enter your other names">
						<has-error :form="form" field="additional_names"></has-error>
					</div>
					<div class="form-group col-md-6">
						<label>Date of Birth</label>
						<input v-model="form.birth_date" type="date" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('birth_date') }" placeholder="Enter your birth date" required>
						<has-error :form="form" field="birth_date"></has-error>
					</div>
					<div class="form-group col-md-6">
						<label>Email Address</label>
						<input v-model="form.email" type="email" class="form-control shadow-sm" :class="{ 'is-invalid': form.errors.has('email') }" placeholder="Enter your email address" readonly required>
						<has-error :form="form" field="email"></has-error>
					</div>
					<div class="form-group col-md-6">
						<label>Phone Number</label>
						<phone-number-component :country="form.country" :phone-number="form.phone" @update-phone-country="updatePhoneCountry" @update-phone="updatePhone" :readonly="false"></phone-number-component>
						<has-error :form="form" field="phone"></has-error>
					</div>
                    <div class="form-group col-md-6">
						<label>Country</label>
						<country-picker-component :countries="options.countries" :country-selected="form.country" @update-country="updateCountry"></country-picker-component>
						<has-error :form="form" field="country"></has-error>
					</div>
                    <div class="form-group col-md-6">
						<label>City/Town</label>
						<city-picker-component :cities="options.cities" :country-selected="form.country_iso" :city-selected="form.city" @update-city="updateCity"></city-picker-component>
						<has-error :form="form" field="city"></has-error>
					</div>
					<div class="form-group col-md-6">
						<label>Nationality</label>
						<select v-model="form.nationality" class="form-control custom-select shadow-sm" :class="{ 'is-invalid': form.errors.has('nationality') }">
                            <option selected :value="null" disabled>Select an option</option>
							<option v-for="option in options.countries" :value="option.nationality">{{ option.nationality }}</option>
                        </select>
						<has-error :form="form" field="nationality"></has-error>
					</div>
					
					<div class="col-12 border-top mt-3">
						<button-component :busy="form.busy" :label="'Save'" :label-loading="'Saving changes...'" :button-class="'btn btn-default float-right mt-3 shadow-sm'"></button-component>
					</div>
				</form>
            </div>
        </div>
	</div>
</template>

<style scoped>
	@media (max-width: 425px) {
		.hide {
			display: none;
		}
	}
</style>

<script>
	export default {
		data() {
			return {
				loading: false,
				error: null,
				options: null,
				user: null,
				form: new Form({
					id: null,
					first_name: null,
					last_name: null,
					additional_names: null,
					birth_date: null,
					email: null,
					phone: null,
					other_phone: null,
					country: null,
					country_iso: null,
					city: null,
					nationality: null,
				}),
			};
		},
		created() {
			this.fetchData();
		},
		watch: {
			$route: "fetchData",
		},
		methods: {
			updateCountry: function (country) {
				this.form.country = country.code;
				this.form.country_iso = country.iso;
			},
			updateCity: function (city) {
				this.form.city = city;
			},
			updatePhoneCountry(value) {
				this.form.country = value;
			},
			updatePhone(value) {
				this.form.phone = value;
			},
			fetchUserInformationOptions() {
				return axios.get("/api/options/user");
			},
			fetchUserIformation() {
				return axios.get("/api/user");
			},
			fetchData() {
				this.error = null;
				this.loading = true;

				axios
					.all([
						this.fetchUserInformationOptions(),
						this.fetchUserIformation(),
					])
					.then(
						axios.spread((options, user) => {
							this.options = options.data.data.options;
							this.user = user.data.data.user.client;

							if (!_.isEmpty(this.user)) {
								this.form.fill({
									id: this.user.id,
									first_name: this.user.first_name,
									last_name: this.user.last_name,
									additional_names: this.user.additional_names,
									birth_date: this.user.birth_date,
									email: this.user.email,
									phone: this.user.phone,
									other_phone: this.user.other_phone,
									country: this.user.country,
									country_iso: this.user.country_details.code_iso,
									city: this.user.city,
									nationality: this.user.nationality,
								});
							}
						})
					)
					.finally(() => {
						this.loading = false;
					});
			},
			save() {
				this.form.patch("/api/user").finally(() => {
					window.scrollTo(0, 0);
				});
			},
		},
	};
</script>
